import { useCallback, useState, useRef } from 'react';
import { toast } from 'sonner';

const ACCEPTED_EXTENSIONS = ['.txt', '.pdf', '.docx'];

interface TranscriptUploadZoneProps {
  onTextExtracted: (text: string, fileName: string) => void;
  isProcessing: boolean;
}

export function TranscriptUploadZone({ onTextExtracted, isProcessing }: TranscriptUploadZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const extractTextFromFile = useCallback(async (file: File) => {
    const ext = file.name.toLowerCase().split('.').pop();

    if (ext === 'txt') {
      const text = await file.text();
      if (!text.trim()) {
        toast.error('O arquivo está vazio.');
        return;
      }
      setFileName(file.name);
      onTextExtracted(text.trim(), file.name);
      return;
    }

    if (ext === 'pdf') {
      // Ler PDF como texto usando pdf.js via CDN
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdfjsLib = (window as any).pdfjsLib;
        if (!pdfjsLib) {
          toast.error('Biblioteca PDF não carregada. Tente colar o texto manualmente.');
          return;
        }
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          const pageText = content.items.map((item: any) => item.str).join(' ');
          fullText += pageText + '\n';
        }
        if (!fullText.trim()) {
          toast.error('Não foi possível extrair texto do PDF. Tente colar manualmente.');
          return;
        }
        setFileName(file.name);
        onTextExtracted(fullText.trim(), file.name);
      } catch {
        toast.error('Erro ao ler PDF. Tente colar o texto manualmente.');
      }
      return;
    }

    if (ext === 'docx') {
      // Ler DOCX extraindo texto do XML interno
      try {
        const JSZip = (window as any).JSZip;
        if (!JSZip) {
          toast.error('Biblioteca DOCX não carregada. Tente colar o texto manualmente.');
          return;
        }
        const zip = await JSZip.loadAsync(file);
        const docXml = await zip.file('word/document.xml')?.async('string');
        if (!docXml) {
          toast.error('Arquivo DOCX inválido.');
          return;
        }
        // Extrair texto dos tags <w:t>
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(docXml, 'application/xml');
        const textNodes = xmlDoc.getElementsByTagName('w:t');
        let fullText = '';
        for (let i = 0; i < textNodes.length; i++) {
          fullText += textNodes[i].textContent || '';
        }
        if (!fullText.trim()) {
          toast.error('Não foi possível extrair texto do DOCX. Tente colar manualmente.');
          return;
        }
        setFileName(file.name);
        onTextExtracted(fullText.trim(), file.name);
      } catch {
        toast.error('Erro ao ler DOCX. Tente colar o texto manualmente.');
      }
      return;
    }

    toast.error('Formato não suportado. Use PDF, TXT ou DOCX.');
  }, [onTextExtracted]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) extractTextFromFile(file);
  }, [extractTextFromFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) extractTextFromFile(file);
    e.target.value = '';
  }, [extractTextFromFile]);

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={handleDrop}
        onClick={() => !isProcessing && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
        } ${isProcessing ? 'pointer-events-none opacity-60' : ''}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />
        <div className="text-4xl mb-2">📄</div>
        <p className="text-sm font-medium">
          {fileName ? `✅ ${fileName}` : 'Arraste o arquivo aqui ou clique para selecionar'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          PDF, TXT ou DOCX — Transcrição ou resumo da entrevista (Zoom, Meet, Teams)
        </p>
      </div>
    </div>
  );
}
